// Post-write print setup for the .xlsx exports.
//
// Why this module exists: our SheetJS build (community 0.18.5) cannot write
// page setup. Verified empirically — `pageSetup`, `printOptions`, `definedNames`
// and frozen panes are silently dropped under every `ws['!…']` key, while
// `!autofilter`, `!margins`, `!cols` and cell `.z` formats do survive. Without
// page setup, Excel prints the 9-column transaction sheet with its defaults:
// portrait, unscaled, no repeated header, which splits ~16.5in of columns into
// three separate page runs and puts the sort keys a hundred pages from the
// dates.
//
// So we patch the OOXML after the workbook is serialized. Alternatives weighed
// and rejected: rewriting the 791-line builder onto exceljs (far more blast
// radius than four XML elements justify), and reaching for fflate (present in
// node_modules only as a nested transitive of jspdf -- a phantom dependency
// that breaks the moment npm hoists differently, and declaring it properly
// meant a 250-entry AWS-SDK dedup in the lockfile).
//
// The zip work therefore goes through XLSX.CFB, a documented export of the
// library we already depend on. No new dependency, no lockfile change.
//
// OOXML enforces child element ORDER. A misplaced element does not degrade —
// Excel refuses the file as corrupt. Every insertion below is anchored to a
// schema-legal position, and the one that cannot find its anchor is SKIPPED
// rather than guessed at: a workbook that prints badly is recoverable, one that
// will not open is not.
//
// CT_Worksheet child order (abridged, the part that matters here):
//   sheetPr, dimension, sheetViews, sheetFormatPr, cols, sheetData, …,
//   autoFilter, …, hyperlinks, printOptions, pageMargins, pageSetup,
//   headerFooter, rowBreaks, colBreaks, …, drawing
import * as XLSX from 'xlsx';

// TextDecoder/TextEncoder exist in every browser we support and are the fast
// path, but they are absent from the jsdom build the test runner uses. Rather
// than polyfill a global for all 337 suites, fall back to hand-rolled UTF-8 —
// which the tests then actually exercise instead of leaving it as dead code.
const hasTextCodecs =
  typeof TextDecoder !== 'undefined' && typeof TextEncoder !== 'undefined';

function decodeUtf8Manual(bytes) {
  const chunks = [];
  const codeUnits = [];
  for (let i = 0; i < bytes.length; ) {
    const byte = bytes[i++];
    let cp;
    if (byte < 0x80) cp = byte;
    else if (byte < 0xe0) cp = ((byte & 0x1f) << 6) | (bytes[i++] & 0x3f);
    else if (byte < 0xf0) {
      cp = ((byte & 0x0f) << 12) | ((bytes[i++] & 0x3f) << 6) | (bytes[i++] & 0x3f);
    } else {
      cp =
        ((byte & 0x07) << 18) |
        ((bytes[i++] & 0x3f) << 12) |
        ((bytes[i++] & 0x3f) << 6) |
        (bytes[i++] & 0x3f);
    }

    if (cp > 0xffff) {
      const offset = cp - 0x10000;
      codeUnits.push(0xd800 + (offset >> 10), 0xdc00 + (offset & 0x3ff));
    } else {
      codeUnits.push(cp);
    }

    // Flush in blocks: one giant fromCharCode.apply blows the argument limit,
    // and one += per character is needlessly slow on a megabyte of sheet XML.
    if (codeUnits.length >= 8192) {
      chunks.push(String.fromCharCode.apply(null, codeUnits));
      codeUnits.length = 0;
    }
  }
  if (codeUnits.length) chunks.push(String.fromCharCode.apply(null, codeUnits));
  return chunks.join('');
}

function encodeUtf8Manual(text) {
  const out = [];
  for (let i = 0; i < text.length; i += 1) {
    let cp = text.charCodeAt(i);
    // Recombine a surrogate pair into its code point.
    if (cp >= 0xd800 && cp <= 0xdbff && i + 1 < text.length) {
      const low = text.charCodeAt(i + 1);
      if (low >= 0xdc00 && low <= 0xdfff) {
        cp = 0x10000 + ((cp - 0xd800) << 10) + (low - 0xdc00);
        i += 1;
      }
    }

    if (cp < 0x80) out.push(cp);
    else if (cp < 0x800) out.push(0xc0 | (cp >> 6), 0x80 | (cp & 0x3f));
    else if (cp < 0x10000) {
      out.push(0xe0 | (cp >> 12), 0x80 | ((cp >> 6) & 0x3f), 0x80 | (cp & 0x3f));
    } else {
      out.push(
        0xf0 | (cp >> 18),
        0x80 | ((cp >> 12) & 0x3f),
        0x80 | ((cp >> 6) & 0x3f),
        0x80 | (cp & 0x3f),
      );
    }
  }
  return new Uint8Array(out);
}

const decodeUtf8 = (bytes) =>
  hasTextCodecs ? new TextDecoder('utf-8').decode(bytes) : decodeUtf8Manual(bytes);

const encodeUtf8 = (text) =>
  hasTextCodecs ? new TextEncoder().encode(text) : encodeUtf8Manual(text);

// Minimal read/write view over the .xlsx zip container, so the patching logic
// below never touches the zip library directly.
function openWorkbookZip(bytes) {
  const container = XLSX.CFB.read(
    bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes),
    { type: 'array' },
  );

  const entry = (path) => XLSX.CFB.find(container, `/${path}`);

  return {
    paths: () => container.FullPaths.slice(),
    read(path) {
      const found = entry(path);
      return found?.content ? decodeUtf8(found.content) : undefined;
    },
    write(path, xml) {
      const found = entry(path);
      if (!found) return false;
      const encoded = encodeUtf8(xml);
      found.content = encoded;
      found.size = encoded.length;
      return true;
    },
    serialize() {
      const out = XLSX.CFB.write(container, { type: 'array', fileType: 'zip' });
      return out instanceof Uint8Array ? out : new Uint8Array(out);
    },
  };
}

const WORKBOOK_PATH = 'xl/workbook.xml';
const WORKBOOK_RELS_PATH = 'xl/_rels/workbook.xml.rels';

const attr = (tag, name) => {
  const match = new RegExp(`\\b${name}="([^"]*)"`).exec(tag);
  return match ? match[1] : undefined;
};

const decodeXmlEntities = (value) =>
  (value || '')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, '&');

const encodeXmlEntities = (value) =>
  (value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

// A sheet reference inside a defined name is always safe to quote, and a
// literal single quote in the name doubles. 'My ''Odd'' Sheet'!$1:$1
const quoteSheetRef = (name) => `'${String(name).replace(/'/g, "''")}'`;

/**
 * Map each sheet NAME to its zip entry path and its zero-based index (which is
 * the `localSheetId` a defined name has to reference).
 *
 * Resolved through workbook.xml + its rels rather than assuming that the Nth
 * appended sheet is sheetN.xml — true for SheetJS today, not guaranteed.
 */
export function resolveSheets(zip) {
  const workbookXml = zip.read(WORKBOOK_PATH);
  const relsXml = zip.read(WORKBOOK_RELS_PATH);
  if (!workbookXml || !relsXml) return [];

  const targetsById = {};
  (relsXml.match(/<Relationship\b[^>]*>/g) || []).forEach((tag) => {
    const id = attr(tag, 'Id');
    const target = attr(tag, 'Target');
    if (id && target) targetsById[id] = target;
  });

  const sheets = [];
  (workbookXml.match(/<sheet\b[^>]*?\/?>/g) || []).forEach((tag, index) => {
    const name = decodeXmlEntities(attr(tag, 'name'));
    // The r: prefix is conventional but not mandated, so match loosely.
    const relId = attr(tag, 'r:id') || attr(tag, 'id');
    const target = relId && targetsById[relId];
    if (!name || !target) return;

    // Targets are workbook-relative ("worksheets/sheet1.xml") or absolute
    // ("/xl/worksheets/sheet1.xml").
    const path = target.startsWith('/')
      ? target.replace(/^\//, '')
      : `xl/${target.replace(/^\.\//, '')}`;

    sheets.push({ name, path, localSheetId: index });
  });

  return sheets;
}

function patchWorksheetXml(xml, { orientation, fitToWidth, freezeRows }) {
  let out = xml;
  const skipped = [];

  // fitToPage lives on sheetPr, which must be the FIRST child of <worksheet>.
  if (fitToWidth) {
    if (/<sheetPr\b/.test(out)) {
      // SheetJS does not emit sheetPr today; if that changes, merging into an
      // existing one is a different job than inserting, so skip loudly.
      skipped.push('sheetPr already present');
    } else {
      out = out.replace(
        /(<worksheet\b[^>]*>)/,
        '$1<sheetPr><pageSetUpPr fitToPage="1"/></sheetPr>',
      );
    }
  }

  // <pane> must be the first child of <sheetView>, which SheetJS emits
  // self-closing — expand it before inserting.
  if (freezeRows) {
    const pane =
      `<pane ySplit="${freezeRows}" topLeftCell="A${freezeRows + 1}"` +
      ' activePane="bottomLeft" state="frozen"/>';

    if (/<sheetView\b[^>]*\/>/.test(out)) {
      out = out.replace(/<sheetView\b([^>]*?)\/>/, `<sheetView$1>${pane}</sheetView>`);
    } else if (/<sheetView\b[^>]*>/.test(out)) {
      out = out.replace(/(<sheetView\b[^>]*>)/, `$1${pane}`);
    } else {
      skipped.push('no sheetView to freeze');
    }
  }

  // pageSetup must sit immediately after pageMargins. We deliberately do NOT
  // synthesize an anchor: without pageMargins the only remaining insertion
  // point is before </worksheet>, which is AFTER headerFooter/rowBreaks/drawing
  // in the schema and would risk an unopenable file. The builder sets
  // ws['!margins'] precisely so this anchor exists.
  const pageSetup =
    `<pageSetup paperSize="1" orientation="${orientation || 'landscape'}"` +
    (fitToWidth ? ` fitToWidth="${fitToWidth}" fitToHeight="0"` : '') +
    '/>';

  if (/<\/pageMargins>/.test(out)) {
    out = out.replace('</pageMargins>', `</pageMargins>${pageSetup}`);
  } else if (/<pageMargins\b[^>]*\/>/.test(out)) {
    out = out.replace(/(<pageMargins\b[^>]*\/>)/, `$1${pageSetup}`);
  } else {
    skipped.push('no pageMargins anchor for pageSetup');
  }

  return { xml: out, skipped };
}

function patchWorkbookXml(xml, titleEntries) {
  if (!titleEntries.length) return { xml, skipped: [] };
  const skipped = [];

  const defs = titleEntries
    .filter(({ localSheetId }) => {
      // Excel treats a duplicate defined name as a repair-on-open error, so
      // never add a second Print_Titles for the same sheet.
      const existing = new RegExp(
        `<definedName name="_xlnm\\.Print_Titles" localSheetId="${localSheetId}"`,
      );
      if (existing.test(xml)) {
        skipped.push(`print titles already defined for localSheetId ${localSheetId}`);
        return false;
      }
      return true;
    })
    .map(({ localSheetId, name, rows }) => {
      const [from, to] = String(rows).split(':');
      const ref = `${quoteSheetRef(name)}!$${from}:$${to || from}`;
      return (
        `<definedName name="_xlnm.Print_Titles" localSheetId="${localSheetId}">` +
        `${encodeXmlEntities(ref)}</definedName>`
      );
    })
    .join('');

  if (!defs) return { xml, skipped };

  // MERGE into an existing block rather than bail out. Setting !autofilter
  // makes SheetJS emit its own <definedNames> holding _xlnm._FilterDatabase,
  // so on the sheet we care about this block almost always already exists --
  // skipping here would make autofilter and repeat-header mutually exclusive.
  // CT_DefinedNames is an unordered sequence, so appending is safe.
  if (/<definedNames>/.test(xml)) {
    return { xml: xml.replace('</definedNames>', `${defs}</definedNames>`), skipped };
  }
  if (/<definedNames\s*\/>/.test(xml)) {
    return {
      xml: xml.replace(/<definedNames\s*\/>/, `<definedNames>${defs}</definedNames>`),
      skipped,
    };
  }
  // definedNames goes after </sheets> and before <calcPr>.
  if (/<\/sheets>/.test(xml)) {
    return {
      xml: xml.replace('</sheets>', `</sheets><definedNames>${defs}</definedNames>`),
      skipped,
    };
  }

  skipped.push('no anchor for definedNames');
  return { xml, skipped };
}

/**
 * Bake print setup into an already-serialized .xlsx.
 *
 * @param {ArrayBuffer|Uint8Array} bytes  the workbook as written by XLSX.write
 * @param {Array<{sheet: string, orientation?: 'landscape'|'portrait',
 *                fitToWidth?: number, freezeRows?: number,
 *                printTitleRows?: string}>} setups  per-sheet, matched by NAME
 * @returns {Uint8Array} a new workbook; the input is not modified
 *
 * Sheets not named in `setups` are left byte-identical, so adding this to the
 * shared download path cannot change an export that does not opt in.
 */
export function applyExcelPrintSetup(bytes, setups) {
  const input = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  if (!setups?.length) return input;

  const zip = openWorkbookZip(input);
  const byName = new Map(resolveSheets(zip).map((s) => [s.name, s]));
  const titleEntries = [];
  const skipped = [];

  setups.forEach((setup) => {
    const sheet = byName.get(setup.sheet);
    const xml = sheet && zip.read(sheet.path);
    if (!xml) {
      skipped.push(`sheet not found: ${setup.sheet}`);
      return;
    }

    const result = patchWorksheetXml(xml, setup);
    zip.write(sheet.path, result.xml);
    skipped.push(...result.skipped.map((reason) => `${setup.sheet}: ${reason}`));

    if (setup.printTitleRows) {
      titleEntries.push({
        localSheetId: sheet.localSheetId,
        name: sheet.name,
        rows: setup.printTitleRows,
      });
    }
  });

  const workbookResult = patchWorkbookXml(zip.read(WORKBOOK_PATH), titleEntries);
  zip.write(WORKBOOK_PATH, workbookResult.xml);
  skipped.push(...workbookResult.skipped);

  if (skipped.length) {
    // Diagnostic only. Every skip leaves a VALID workbook that merely prints
    // with Excel's defaults, so this must never throw on an export path.
    // eslint-disable-next-line no-console
    console.warn('[xlsxPrintSetup] skipped:', skipped.join('; '));
  }

  return zip.serialize();
}

export default applyExcelPrintSetup;
